import { z } from 'zod';

const inlineText = z.string().trim().min(1).max(8_000);
const documentBlockSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('heading'), level: z.number().int().min(1).max(3), text: inlineText }).strict(),
  z.object({ type: z.literal('paragraph'), text: inlineText }).strict(),
  z.object({ type: z.literal('list'), style: z.enum(['bulleted', 'numbered']), items: z.array(inlineText).min(1).max(50) }).strict(),
  z.object({ type: z.literal('table'), columns: z.array(inlineText.max(120)).min(1).max(12), rows: z.array(z.array(z.string().max(2_000)).max(12)).max(200) }).strict(),
]);

export const documentArtifactInputSchema = z.object({
  title: z.string().trim().min(1).max(160),
  summary: z.string().trim().min(1).max(2_000),
  blocks: z.array(documentBlockSchema).min(1).max(120),
  citations: z.array(z.object({ label: z.string().trim().min(1).max(200), url: z.string().url().max(2_000) }).strict()).max(40).default([]),
}).strict();

export const documentArtifactToolInputSchema = z.object({
  title: z.string().trim().min(1).max(160),
  summary: z.string().trim().min(1).max(2_000),
  content: z.string().trim().min(1).max(24_000),
  citations: z.array(z.object({ label: z.string().trim().min(1).max(200), url: z.string().url().max(2_000) }).strict()).max(40).default([]),
}).strict();

function markdownContentToBlocks(content: string): z.infer<typeof documentBlockSchema>[] {
  const blocks: z.infer<typeof documentBlockSchema>[] = [];
  const paragraphLines: string[] = [];
  let listStyle: 'bulleted' | 'numbered' | null = null;
  let listItems: string[] = [];
  const flushParagraph = () => {
    const text = paragraphLines.splice(0).join(' ').trim();
    if (text) blocks.push({ type: 'paragraph', text });
  };
  const flushList = () => {
    if (listStyle && listItems.length) blocks.push({ type: 'list', style: listStyle, items: listItems });
    listStyle = null;
    listItems = [];
  };
  for (const rawLine of content.split(/\r?\n/)) {
    const line = rawLine.trim();
    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    const bullet = line.match(/^[-*]\s+(.+)$/);
    const numbered = line.match(/^\d+[.)]\s+(.+)$/);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
    } else if (bullet || numbered) {
      flushParagraph();
      const nextStyle = bullet ? 'bulleted' : 'numbered';
      if (listStyle && listStyle !== nextStyle) flushList();
      listStyle = nextStyle;
      listItems.push((bullet ?? numbered)![1]);
    } else if (!line) {
      flushParagraph();
      flushList();
    } else {
      flushList();
      paragraphLines.push(line);
    }
  }
  flushParagraph();
  flushList();
  return blocks.length ? blocks : [{ type: 'paragraph', text: content.trim() }];
}

export function normalizeDocumentArtifactInput(input: unknown): DocumentArtifactInput {
  const canonical = documentArtifactInputSchema.safeParse(input);
  if (canonical.success) return canonical.data;
  const simple = documentArtifactToolInputSchema.parse(input);
  return documentArtifactInputSchema.parse({
    title: simple.title,
    summary: simple.summary,
    blocks: markdownContentToBlocks(simple.content),
    citations: simple.citations,
  });
}

const spreadsheetScalarSchema = z.union([z.string().max(2_000), z.number().finite(), z.boolean(), z.null()]);
const formulaSchema = z.string().trim().regex(/^(SUM|AVERAGE|MIN|MAX|COUNT)\([A-Z]{1,3}[1-9]\d*(?::[A-Z]{1,3}[1-9]\d*)?\)$/);
const spreadsheetCellSchema = z.union([
  z.object({ value: spreadsheetScalarSchema, format: z.enum(['text', 'number', 'currency', 'percent', 'date']).optional() }).strict(),
  z.object({ formula: formulaSchema, format: z.enum(['number', 'currency', 'percent']).optional() }).strict(),
]);

export const spreadsheetArtifactInputSchema = z.object({
  title: z.string().trim().min(1).max(160),
  summary: z.string().trim().min(1).max(2_000),
  sheets: z.array(z.object({
    name: z.string().trim().min(1).max(60),
    columns: z.array(z.object({ key: z.string().regex(/^[a-z][a-z0-9_]*$/).max(60), label: z.string().trim().min(1).max(120) }).strict()).min(1).max(24),
    rows: z.array(z.record(z.string(), spreadsheetCellSchema)).max(1_000),
  }).strict()).min(1).max(12),
}).strict();

export type DocumentArtifactInput = z.infer<typeof documentArtifactInputSchema>;
export type SpreadsheetArtifactInput = z.infer<typeof spreadsheetArtifactInputSchema>;

export type StructuredAssistantArtifact =
  | ({ artifactId: string; artifactType: 'scarlet.document.v1' } & DocumentArtifactInput)
  | ({ artifactId: string; artifactType: 'scarlet.spreadsheet.v1' } & SpreadsheetArtifactInput);

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

export function parseStructuredArtifact(result: unknown): StructuredAssistantArtifact | null {
  const root = record(result);
  const data = record(root?.data);
  const nested = record(data?.artifact);
  const candidate = nested ?? data;
  if (!candidate || typeof candidate.artifactId !== 'string') return null;
  const { artifactId: _artifactId, artifactType: _artifactType, ...source } = candidate;
  if (candidate.artifactType === 'scarlet.document.v1') {
    const parsed = documentArtifactInputSchema.safeParse(source);
    return parsed.success ? { artifactId: candidate.artifactId, artifactType: candidate.artifactType, ...parsed.data } : null;
  }
  if (candidate.artifactType === 'scarlet.spreadsheet.v1') {
    const parsed = spreadsheetArtifactInputSchema.safeParse(source);
    return parsed.success ? { artifactId: candidate.artifactId, artifactType: candidate.artifactType, ...parsed.data } : null;
  }
  return null;
}

export function documentArtifactToMarkdown(artifact: Extract<StructuredAssistantArtifact, { artifactType: 'scarlet.document.v1' }>): string {
  const lines = [`# ${artifact.title}`, ''];
  for (const block of artifact.blocks) {
    if (block.type === 'heading') lines.push(`${'#'.repeat(block.level)} ${block.text}`, '');
    if (block.type === 'paragraph') lines.push(block.text, '');
    if (block.type === 'list') lines.push(...block.items.map((item, index) => `${block.style === 'numbered' ? `${index + 1}.` : '-'} ${item}`), '');
    if (block.type === 'table') {
      lines.push(`| ${block.columns.join(' | ')} |`, `| ${block.columns.map(() => '---').join(' | ')} |`);
      lines.push(...block.rows.map((row) => `| ${block.columns.map((_, index) => String(row[index] ?? '').replace(/\|/g, '\\|')).join(' | ')} |`), '');
    }
  }
  if (artifact.citations.length) lines.push('## Sources', '', ...artifact.citations.map((citation) => `- [${citation.label}](${citation.url})`), '');
  return lines.join('\n').trimEnd();
}

function cellText(value: unknown): string {
  const cell = record(value);
  if (!cell) return '';
  if (typeof cell.formula === 'string') return `=${cell.formula}`;
  return cell.value === null || cell.value === undefined ? '' : String(cell.value);
}

export function spreadsheetSheetToCsv(artifact: Extract<StructuredAssistantArtifact, { artifactType: 'scarlet.spreadsheet.v1' }>, sheetIndex = 0): string {
  const sheet = artifact.sheets[sheetIndex];
  if (!sheet) return '';
  const escape = (value: string) => /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  return [
    sheet.columns.map((column) => escape(column.label)).join(','),
    ...sheet.rows.map((row) => sheet.columns.map((column) => escape(cellText(row[column.key]))).join(',')),
  ].join('\n');
}
